import { ICommonDates } from '../state/common';
import { OrderStatus } from '../state/orders';
import { IDownloadCSVPayload } from '../state/summary';
import { DateFormat, formatDate, formatLocalDate } from '@telstra-connect/utils/data/date-helpers';
import * as moment from 'moment';
import { IOrderInfoSubstageContent, IOrderInfoPrimaryStage, ISubStage } from '../state/order-info';
import {isOrderStrategic} from './order-type-check';

export enum PayloadOrderStatus {
    AtRisk = 'At risk',
    Delayed = 'Delayed',
    OnTrack = 'On track',
    Complete = 'Complete',
    Withdrawn = 'Withdrawn',
}

export interface ICommonDatesPayload {
    estimatedDelivery?: string;
    requiredDelivery?: string;
    upcomingAppointment?: string;
    // NOTE: for backward compatibility, to be removed
    orderClosedDate?: string;
    // NOTE: to be used instead of `orderClosedDate`
    closedDate?: string;
    estimatedDate?: string;
    requiredDate?: string;
    completedDate?: string;
}

interface IOrderExpressCustomer {
    isLegacy: boolean
}
export interface IOrderExpressPayload {
    data: IOrderExpressCustomer
}

export interface ISummaryPayload {
    ordersClosed: number;
    ordersTotal: number;
    ordersAtRisk: number;
    ordersDelayed: number;
    ordersOnTrack: number;
    isPartialResponse: boolean;
}

const statusesByPayloadOrderStatus: { [key: string]: OrderStatus } = {
    'At risk': OrderStatus.AtRisk,
    'Potential delay': OrderStatus.AtRisk,
    'Delayed': OrderStatus.Delayed,
    'On track': OrderStatus.OnTrack,
    'Completed': OrderStatus.Complete,
    'Withdrawn': OrderStatus.Withdrawn,
    'Fulfilled': OrderStatus.Fulfilled
};

export const mapStatus = (payloadValue: PayloadOrderStatus): OrderStatus =>
    statusesByPayloadOrderStatus[payloadValue] || OrderStatus.Unknown;

export const ESTIMATING = 'Estimating';
export const UNDERREVIEW = 'Under review';
export const NOTAVAILABLE = 'Not available';
export const SITE_NOT_AVAILABLE = 'Site name not available';

export function countSum(arr): number {
    let count = 0;
    arr.forEach((arrayItem) => {
        count = count + parseInt(arrayItem.count, 10);
    });
    return count;
}

export function downloadFileName(params: IDownloadCSVPayload): string {
    let fileName = 'Allorders';
    fileName += (params.type) ? params.type : '';
    fileName += `-${formatLocalDate(moment().format(), DateFormat.DEFAULT_DATE_CSV_BILLING)}`;
    fileName += (params.type === 'pdf') ? '.pdf' : '.csv';
    return fileName;
};

// function titleCase(stringValue) {
//     return stringValue.toLowerCase().split(' ').map((word) => {
//         return (word.charAt(0).toUpperCase() + word.slice(1));
//     }).join(' ');
// }

export function mapDates(dates: ICommonDatesPayload, status?: string, type?: string): ICommonDates {
    let result: ICommonDates = {};
    if (status !== OrderStatus.Complete && status !== OrderStatus.Withdrawn &&
        status !== OrderStatus.WithdrawnInProgress) {
        if (type === 'products') {
            result = {
                productEstimatedDelivery: (dates.estimatedDate &&
                    formatDate(dates.estimatedDate)) ||
                    ESTIMATING,
                productRequiredDelivery: (dates.requiredDate &&
                    formatDate(dates.requiredDate)) ||
                    UNDERREVIEW,
                upcomingAppointment: (dates.upcomingAppointment &&
                    formatDate(dates.upcomingAppointment) || '-'),
            };
        } else {
            result = {
                estimatedDelivery: (dates.estimatedDelivery &&
                    formatDate(dates.estimatedDelivery)) ||
                    ESTIMATING,
                requiredDelivery: (dates.requiredDelivery &&
                    formatDate(dates.requiredDelivery)) ||
                    UNDERREVIEW
            };
        }
    } else {
        result.estimatedDelivery = ''
        result.requiredDelivery = ''
        if (type === 'products') {
            result.upcomingAppointment = dates.upcomingAppointment ? formatDate(dates.upcomingAppointment) : '-';
            result.productClosedDate = dates.completedDate ? formatDate(dates.completedDate) : '-';
        } else {
            if ('orderClosedDate' in dates) {
                result.closedDate = formatDate(dates.orderClosedDate);
            } else if ('closedDate' in dates) {
                result.closedDate = formatDate(dates.closedDate);
            } else {
                result.closedDate = NOTAVAILABLE;
            }
        }
    }
    return result;
}

export function mappedPrimaryStage(substageContent: IOrderInfoSubstageContent[],
    emptyStageContent: IOrderInfoSubstageContent[],
    primaryStage: IOrderInfoPrimaryStage[], productType: string, hardwareDelivery,
    orderNumber: string, billingStartDate: string, orderType: string):
        IOrderInfoPrimaryStage[] {
    const mappedContent = primaryStage.map((productStage) => {
        const emptyCheck = productStage.substages.length === 0 ? true : false;
        return {
            ...productStage,
            subHeader: isOrderStrategic(orderNumber) === true ? '' : productStage.substages.length &&
                productStage.status === 'Complete' ? 'Completed' : productStage.status === 'Withdrawn' ? 'Withdrawn'
                    : mappedSubHeader(productStage.substages),
            substages: emptyCheck
                ? mappedEmptySubstage(productStage.name, emptyStageContent, productStage.status, productType)
                : productStage.substages.map((productSubStage) => {
                        return {
                            ...productSubStage,
                            content:
                            (substageContent[productSubStage.name.toLowerCase()] &&
                            substageContent[productSubStage.name.toLowerCase()].mainContent &&
                            ((orderType &&
                            mapSubstageMainContent(
                                substageContent,
                                productSubStage,
                                orderType,
                                billingStartDate))
                            || ''))
                            || substageContent[productSubStage.name.toLowerCase()] &&
                            substageContent[productSubStage.name.toLowerCase()].content || ''
                        }
                })
        }
    }
    );
    return mappedContent;
}

function mappedEmptySubstage(primaryStage: string,
    emptyStageContent: IOrderInfoSubstageContent[], status: string, productType: string):
    ISubStage[] {
    return [{
        name: emptyStageContent[primaryStage.toLowerCase()].name,
        status: status === 'Complete' ? 'Completed' : primaryStage === 'Received' ? 'In Progress' : 'Incomplete',
        content: emptyStageContent[primaryStage.toLowerCase()].mainContent[status.toLowerCase()] ||
            emptyStageContent[primaryStage.toLowerCase()].mainContent.default,
        subContent: primaryStage === 'Fulfilled' ? productType === 'Cancel' ? [] :
            status === 'Complete' ? emptyStageContent[primaryStage.toLowerCase()].subContent : []
            : emptyStageContent[primaryStage.toLowerCase()].subContent
    }]
}

function mappedSubHeader(subStages: ISubStage[]) {
    let inProgressIndex = 0;
    let step = 0;
    for (const substage of subStages) {
        if (substage.status === 'Completed') {
            step = 1;
            inProgressIndex = inProgressIndex + 1;
        }
    }
    inProgressIndex = inProgressIndex + step;
    return 'Step ' + inProgressIndex + ' of ' + subStages.length;
}

function mapSubstageMainContent(
    substageContent: IOrderInfoSubstageContent[],
    productSubStage: ISubStage,
    orderType: string,
    billingStartDate: string) {
    // completed, default, in progress
    const mainContent = substageContent[productSubStage.name.toLowerCase()].mainContent

    // status Completed, In Progress, Not Started
    // taking new as default and modify as modify
    const type = productSubStage.status.toLowerCase() === 'completed' ? productSubStage.status.toLowerCase() :
        ['new provide', 'new', 'modify', 'cancel'].includes(orderType.toLocaleLowerCase()) ||
        productSubStage.status.toLowerCase() === 'in progress' ||
        productSubStage.status.toLowerCase() === 'not started' ? 'default' : orderType.toLocaleLowerCase();

    return(
        (typeof(mainContent) === 'object' &&
            (mainContent[type].includes('{{BILLING_COMMENCEMENT_DATE}}') &&
            (mainContent[type].replace('{{BILLING_COMMENCEMENT_DATE}}',
                (billingStartDate && formatDate(billingStartDate)) || '')))
            ||
            (mainContent[type]))
            ||
        (mainContent.default)
    )
}
